import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

const API = (path, opts = {}) =>
  fetch(path, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
    ...opts,
  });

const COMMISSION_TYPES = ['fixed', 'percentage'];

const emptyPartnerForm = () => ({
  category_id: '', name: '', website: '', contact_name: '', contact_email: '',
  description: '', about: '', usp_bullets: [''], services: [{ name: '', description: '', price: '' }],
  commission_type: 'fixed', commission_value: '', commission_notes: '', notes: '',
});

export default function OwnerPartners() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'partners';
  const setTab = (t) => setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('tab', t); p.delete('company'); return p; });
  const [categories, setCategories] = useState([]);
  const [partners, setPartners] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyPartners, setCompanyPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('all');
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Category modal
  const [showCatModal, setShowCatModal] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [catForm, setCatForm] = useState({ name: '', icon: '', description: '' });

  // Partner modal
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [editPartner, setEditPartner] = useState(null);
  const [partnerForm, setPartnerForm] = useState(emptyPartnerForm());
  const [aiFilling, setAiFilling] = useState(false);
  const [aiUrl, setAiUrl] = useState('');
  const [aiError, setAiError] = useState('');

  // Assign tab — commission editing
  const [editingCommission, setEditingCommission] = useState(null); // partnerId
  const [commissionForm, setCommissionForm] = useState({ commission_type: 'fixed', commission_value: '', commission_notes: '' });

  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState({});
  const [formError, setFormError] = useState('');

  // Invite modal
  const [invitePartner, setInvitePartner] = useState(null);
  const [inviteForm, setInviteForm] = useState({ contact_name: '', email: '' });
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // { link } or null
  const [inviteError, setInviteError] = useState('');

  const openInviteModal = (p) => {
    setInvitePartner(p);
    setInviteForm({ contact_name: p.contact_name || '', email: p.contact_email || '' });
    setInviteResult(null); setInviteError('');
  };

  const sendInvite = async () => {
    if (!inviteForm.contact_name.trim() || !inviteForm.email.trim()) {
      return setInviteError('Contact name and email are required');
    }
    setInviteSending(true); setInviteError('');
    try {
      const r = await API('/api/partners/invite-send', {
        method: 'POST',
        body: JSON.stringify({ partner_id: invitePartner.id, contact_name: inviteForm.contact_name.trim(), email: inviteForm.email.trim() }),
      });
      const d = await r.json();
      if (!r.ok) { setInviteError(d.error || 'Failed to send invite'); }
      else { setInviteResult({ link: d.signupLink }); }
    } catch { setInviteError('Network error'); }
    setInviteSending(false);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    const [catsRes, partnersRes, companiesRes] = await Promise.all([
      API('/api/partners/categories'),
      API('/api/partners/list'),
      API('/api/owner/companies'),
    ]);
    const [cats, parts, comps] = await Promise.all([catsRes.json(), partnersRes.json(), companiesRes.json()]);
    setCategories(Array.isArray(cats) ? cats : []);
    setPartners(Array.isArray(parts) ? parts : []);
    setCompanies(Array.isArray(comps) ? comps : (comps.companies || []));
    setLoading(false);
  }, []);


  const loadAnalytics = async (period = 'all') => {
    setAnalyticsLoading(true);
    try {
      const r = await API(`/api/partners/analytics?period=${period}`);
      const d = await r.json();
      setAnalyticsData(d);
    } catch(e) { console.error(e); }
    setAnalyticsLoading(false);
  };

  useEffect(() => {
    if (tab === 'analytics') loadAnalytics(analyticsPeriod);
  }, [tab, analyticsPeriod]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Restore selected company from URL after companies load
  useEffect(() => {
    const companyId = searchParams.get('company');
    if (companyId && companies.length > 0 && !selectedCompany) {
      const found = companies.find(c => c.id === companyId);
      if (found) { setSelectedCompany(found); loadCompanyPartners(found.id); }
    }
  }, [companies, searchParams]);

  const loadCompanyPartners = async (companyId) => {
    const r = await API(`/api/partners/company/${companyId}`);
    const d = await r.json();
    setCompanyPartners(Array.isArray(d) ? d : []);
  };

  const handleSelectCompany = (company) => {
    setSelectedCompany(company);
    setEditingCommission(null);
    loadCompanyPartners(company.id);
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('tab', 'assign'); p.set('company', company.id); return p; });
  };

  // ── Category CRUD ──────────────────────────────────────────
  const openNewCat = () => { setCatForm({ name: '', icon: '', description: '' }); setEditCat(null); setFormError(''); setShowCatModal(true); };
  const openEditCat = (c) => { setCatForm({ name: c.name, icon: c.icon || '', description: c.description || '' }); setEditCat(c); setFormError(''); setShowCatModal(true); };
  const saveCat = async (e) => {
    e.preventDefault(); setFormError(''); setSaving(true);
    const r = await API(editCat ? `/api/partners/categories/${editCat.id}` : '/api/partners/categories', {
      method: editCat ? 'PATCH' : 'POST', body: JSON.stringify(catForm),
    });
    const d = await r.json();
    setSaving(false);
    if (!r.ok) { setFormError(d.error || 'Failed'); return; }
    setShowCatModal(false); loadAll();
  };
  const toggleCat = async (cat) => {
    await API(`/api/partners/categories/${cat.id}`, { method: 'PATCH', body: JSON.stringify({ active: !cat.active }) });
    loadAll();
  };

  // ── Partner CRUD ──────────────────────────────────────────
  const openNewPartner = () => {
    const f = emptyPartnerForm();
    f.category_id = categories[0]?.id || '';
    setPartnerForm(f); setEditPartner(null); setFormError(''); setAiError(''); setAiUrl(''); setShowPartnerModal(true);
  };
  const openEditPartner = (p) => {
    setPartnerForm({
      category_id: p.category_id, name: p.name, website: p.website || '',
      contact_name: p.contact_name || '', contact_email: p.contact_email || '',
      description: p.description || '', about: p.about || '',
      usp_bullets: (p.usp_bullets && p.usp_bullets.length) ? p.usp_bullets : [''],
      services: (p.services && p.services.length) ? p.services : [{ name: '', description: '', price: '' }],
      commission_type: p.commission_type || 'fixed', commission_value: p.commission_value || '',
      commission_notes: p.commission_notes || '', notes: p.notes || '',
    });
    setEditPartner(p); setFormError(''); setAiError(''); setAiUrl(''); setShowPartnerModal(true);
  };

  const savePartner = async (e) => {
    e.preventDefault(); setFormError(''); setSaving(true);
    const body = {
      ...partnerForm,
      commission_value: partnerForm.commission_value ? parseFloat(partnerForm.commission_value) : 0,
      usp_bullets: partnerForm.usp_bullets.filter(b => b.trim()),
      services: partnerForm.services.filter(s => s.name.trim()),
    };
    const r = await API(editPartner ? `/api/partners/list/${editPartner.id}` : '/api/partners/list', {
      method: editPartner ? 'PATCH' : 'POST', body: JSON.stringify(body),
    });
    const d = await r.json();
    setSaving(false);
    if (!r.ok) { setFormError(d.error || 'Failed'); return; }
    setShowPartnerModal(false); loadAll();
  };

  const togglePartnerActive = async (p) => {
    await API(`/api/partners/list/${p.id}`, { method: 'PATCH', body: JSON.stringify({ active: !p.active }) });
    loadAll();
  };

  // ── AI Auto-fill ──────────────────────────────────────────
  const runAiFill = async () => {
    if (!aiUrl) { setAiError('Enter the partner website URL first'); return; }
    setAiFilling(true); setAiError('');
    try {
      const r = await API('/api/partners/ai-profile', { method: 'POST', body: JSON.stringify({ url: aiUrl }) });
      const d = await r.json();
      if (!r.ok) { setAiError(d.error || 'AI fill failed'); setAiFilling(false); return; }
      setPartnerForm(f => ({
        ...f,
        website: aiUrl,
        about: d.about || f.about,
        usp_bullets: (d.usp_bullets && d.usp_bullets.length) ? d.usp_bullets : f.usp_bullets,
        services: (d.services && d.services.length) ? d.services : f.services,
        commission_notes: d.commission_notes || f.commission_notes,
        contact_name: d.contact_name || f.contact_name,
        contact_email: d.contact_email || f.contact_email,
      }));
    } catch (err) {
      setAiError('Network error — check the URL and try again');
    }
    setAiFilling(false);
  };

  // ── USP bullet helpers ────────────────────────────────────
  const setBullet = (i, val) => setPartnerForm(f => { const b = [...f.usp_bullets]; b[i] = val; return { ...f, usp_bullets: b }; });
  const addBullet = () => setPartnerForm(f => ({ ...f, usp_bullets: [...f.usp_bullets, ''] }));
  const removeBullet = (i) => setPartnerForm(f => ({ ...f, usp_bullets: f.usp_bullets.filter((_, j) => j !== i) }));

  // ── Service helpers ───────────────────────────────────────
  const setService = (i, field, val) => setPartnerForm(f => { const s = f.services.map((svc, j) => j === i ? { ...svc, [field]: val } : svc); return { ...f, services: s }; });
  const addService = () => setPartnerForm(f => ({ ...f, services: [...f.services, { name: '', description: '', price: '' }] }));
  const removeService = (i) => setPartnerForm(f => ({ ...f, services: f.services.filter((_, j) => j !== i) }));

  // ── Assign tab — toggle + per-firm commission ─────────────
  const togglePartner = async (companyId, partnerId, currentEnabled) => {
    setToggling(t => ({ ...t, [partnerId]: true }));
    await API(`/api/partners/company/${companyId}/${partnerId}`, {
      method: 'PATCH', body: JSON.stringify({ enabled: !currentEnabled }),
    });
    await loadCompanyPartners(companyId);
    setToggling(t => ({ ...t, [partnerId]: false }));
  };

  const openCommissionEdit = (p) => {
    setCommissionForm({
      commission_type: p.agreed_commission_type || p.default_commission_type || 'fixed',
      commission_value: p.agreed_commission_value ?? p.default_commission_value ?? '',
      commission_notes: p.agreed_commission_notes || '',
    });
    setEditingCommission(p.id);
  };

  const saveCommission = async (companyId, partnerId) => {
    setSaving(true);
    await API(`/api/partners/company/${companyId}/${partnerId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        commission_type: commissionForm.commission_type,
        commission_value: commissionForm.commission_value ? parseFloat(commissionForm.commission_value) : null,
        commission_notes: commissionForm.commission_notes || null,
      }),
    });
    setSaving(false);
    setEditingCommission(null);
    loadCompanyPartners(companyId);
  };

  // Grouped views
  const grouped = categories.reduce((acc, c) => {
    acc[c.id] = { cat: c, items: partners.filter(p => p.category_id === c.id) };
    return acc;
  }, {});

  const groupedCp = companyPartners.reduce((acc, p) => {
    const key = p.category_name;
    if (!acc[key]) acc[key] = { icon: p.category_icon, items: [] };
    acc[key].items.push(p);
    return acc;
  }, {});

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-main">
        <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>Partner Network</h1>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>Manage third-party service partners and assign them to broker firms</p>

        {error && <div style={alertRed}>{error}</div>}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid #e5e7eb' }}>
          {[['partners', 'Partners'], ['categories', 'Categories'], ['assign', 'Assign to Firms'], ['analytics', 'Network Analytics']].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} style={{
              padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: '14px', fontWeight: tab === key ? 700 : 500,
              color: tab === key ? '#10b981' : '#6b7280',
              borderBottom: tab === key ? '2px solid #10b981' : '2px solid transparent',
              marginBottom: '-1px', transition: 'all .15s',
            }}>{label}</button>
          ))}
        </div>

        {loading ? <div style={{ color: '#6b7280', padding: '40px 0' }}>Loading…</div> : (<>

          {/* ── PARTNERS TAB ─────────────────────────────────── */}
          {tab === 'partners' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <button onClick={openNewPartner} style={btnGreen}>+ Add Partner</button>
              </div>
              {Object.values(grouped).map(({ cat, items }) => (
                <div key={cat.id} style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <span style={{ fontSize: '18px' }}>{cat.icon}</span>
                    <span style={{ fontSize: '15px', fontWeight: 700, color: cat.active ? '#0e1b2c' : '#9ca3af' }}>{cat.name}</span>
                    {!cat.active && <span style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>inactive</span>}
                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>({items.length})</span>
                  </div>
                  {items.length === 0 ? (
                    <div style={{ fontSize: '13px', color: '#9ca3af', paddingLeft: '26px' }}>No partners in this category yet.</div>
                  ) : (
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {items.map(p => (
                        <div key={p.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', opacity: p.active ? 1 : 0.6 }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 700, fontSize: '15px', color: '#111827' }}>{p.name}</div>
                              {p.about && <div style={{ fontSize: '13px', color: '#374151', marginTop: '4px', lineHeight: 1.5 }}>{p.about}</div>}
                              {!p.about && p.description && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{p.description}</div>}

                              {/* USP bullets */}
                              {p.usp_bullets && p.usp_bullets.length > 0 && (
                                <ul style={{ margin: '8px 0 0 0', paddingLeft: '18px' }}>
                                  {p.usp_bullets.map((b, i) => (
                                    <li key={i} style={{ fontSize: '12px', color: '#374151', marginBottom: '2px' }}>{b}</li>
                                  ))}
                                </ul>
                              )}

                              {/* Services chips */}
                              {p.services && p.services.length > 0 && (
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                                  {p.services.map((s, i) => (
                                    <span key={i} style={{ fontSize: '11px', background: '#f0fdf4', color: '#065f46', padding: '2px 10px', borderRadius: '999px', border: '1px solid #bbf7d0', fontWeight: 600 }}>
                                      {s.name}{s.price ? ` — ${s.price}` : ''}
                                    </span>
                                  ))}
                                </div>
                              )}

                              <div style={{ display: 'flex', gap: '16px', marginTop: '8px', fontSize: '12px', color: '#9ca3af' }}>
                                {p.contact_name && <span>👤 {p.contact_name}</span>}
                                {p.contact_email && <span>✉ {p.contact_email}</span>}
                                {p.website && <span>🌐 {p.website.replace('https://', '')}</span>}
                                <span style={{ color: '#059669', fontWeight: 600 }}>
                                  Commission: {p.commission_type === 'percentage' ? `${p.commission_value}%` : `£${p.commission_value}`}
                                </span>
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end', flexShrink: 0 }}>
                              <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', fontWeight: 600, background: p.active ? '#d1fae5' : '#f3f4f6', color: p.active ? '#065f46' : '#9ca3af' }}>
                                {p.active ? 'Active' : 'Inactive'}
                              </span>
                              <button onClick={() => openEditPartner(p)} style={btnSmallGhost}>Edit profile</button>
                              <button onClick={() => openInviteModal(p)} style={{ ...btnSmallGhost, color: p.partner_user_id ? '#065f46' : '#6366f1', borderColor: p.partner_user_id ? '#bbf7d0' : '#c7d2fe', background: p.partner_user_id ? '#f0fdf4' : '#eef2ff' }}>
                                {p.partner_user_id ? '✓ Portal active' : 'Invite to portal'}
                              </button>
                              <button onClick={() => togglePartnerActive(p)} style={{ ...btnSmallGhost, color: p.active ? '#991b1b' : '#065f46' }}>
                                {p.active ? 'Deactivate' : 'Activate'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── CATEGORIES TAB ───────────────────────────────── */}
          {tab === 'categories' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <button onClick={openNewCat} style={btnGreen}>+ Add Category</button>
              </div>
              <div style={{ display: 'grid', gap: '8px' }}>
                {categories.map(c => {
                  const pCount = partners.filter(p => p.category_id === c.id).length;
                  return (
                    <div key={c.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '16px', opacity: c.active ? 1 : 0.6 }}>
                      <span style={{ fontSize: '24px', width: '32px', textAlign: 'center' }}>{c.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '14px' }}>{c.name}</div>
                        {c.description && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{c.description}</div>}
                        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{pCount} partner{pCount !== 1 ? 's' : ''}</div>
                      </div>
                      <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', fontWeight: 600, background: c.active ? '#d1fae5' : '#f3f4f6', color: c.active ? '#065f46' : '#9ca3af' }}>
                        {c.active ? 'Active' : 'Inactive'}
                      </span>
                      <button onClick={() => openEditCat(c)} style={btnSmallGhost}>Edit</button>
                      <button onClick={() => toggleCat(c)} style={{ ...btnSmallGhost, color: c.active ? '#991b1b' : '#065f46' }}>
                        {c.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── ASSIGN TAB ───────────────────────────────────── */}
          {tab === 'assign' && (
            <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '20px', alignItems: 'start' }}>
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', fontSize: '12px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  Select Broker Firm
                </div>
                {companies.map(c => (
                  <button key={c.id} onClick={() => handleSelectCompany(c)} style={{
                    width: '100%', padding: '12px 16px', border: 'none', textAlign: 'left',
                    background: selectedCompany?.id === c.id ? '#f0fdf4' : '#fff',
                    borderLeft: selectedCompany?.id === c.id ? '3px solid #10b981' : '3px solid transparent',
                    cursor: 'pointer', fontSize: '13px', fontWeight: selectedCompany?.id === c.id ? 600 : 400,
                    borderBottom: '1px solid #f9fafb', color: '#374151',
                  }}>{c.name}</button>
                ))}
              </div>

              <div>
                {!selectedCompany ? (
                  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '40px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
                    ← Select a broker firm to manage its partners
                  </div>
                ) : (
                  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                    <div style={{ fontSize: '17px', fontWeight: 700, marginBottom: '2px' }}>{selectedCompany.name}</div>
                    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '20px' }}>
                      Toggle partners on/off. Set the agreed commission rate per partner — this is what the partner will pay this firm per referral.
                    </div>

                    {Object.entries(groupedCp).map(([catName, { icon, items }]) => (
                      <div key={catName} style={{ marginBottom: '24px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontSize: '13px', fontWeight: 700, color: '#374151' }}>
                          <span>{icon}</span><span>{catName}</span>
                        </div>
                        {items.map(p => (
                          <div key={p.id} style={{ borderBottom: '1px solid #f3f4f6', paddingBottom: '14px', marginBottom: '14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>{p.name}</div>
                                {p.description && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{p.description}</div>}
                              </div>

                              {/* Commission rate display */}
                              <div style={{ fontSize: '12px', textAlign: 'right', minWidth: '140px' }}>
                                {p.agreed_commission_value != null ? (
                                  <span style={{ color: '#059669', fontWeight: 600 }}>
                                    Agreed: {p.agreed_commission_type === 'percentage' ? `${p.agreed_commission_value}%` : `£${p.agreed_commission_value}`}
                                  </span>
                                ) : (
                                  <span style={{ color: '#9ca3af' }}>
                                    Default: {p.default_commission_type === 'percentage' ? `${p.default_commission_value}%` : `£${p.default_commission_value}`}
                                  </span>
                                )}
                                <br />
                                <button
                                  onClick={() => editingCommission === p.id ? setEditingCommission(null) : openCommissionEdit(p)}
                                  style={{ fontSize: '11px', color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: '2px' }}
                                >
                                  {editingCommission === p.id ? 'Cancel' : 'Set agreed rate'}
                                </button>
                              </div>

                              {/* Toggle */}
                              <button
                                onClick={() => togglePartner(selectedCompany.id, p.id, p.enabled)}
                                disabled={toggling[p.id]}
                                style={{
                                  width: '52px', height: '28px', borderRadius: '14px', border: 'none', cursor: 'pointer',
                                  background: p.enabled ? '#10b981' : '#d1d5db', position: 'relative', transition: 'background .2s', flexShrink: 0,
                                }}
                              >
                                <span style={{
                                  position: 'absolute', top: '4px', left: p.enabled ? '26px' : '4px',
                                  width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
                                  transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', display: 'block',
                                }} />
                              </button>
                              <span style={{ fontSize: '12px', fontWeight: 600, minWidth: '28px', color: p.enabled ? '#065f46' : '#9ca3af' }}>
                                {p.enabled ? 'On' : 'Off'}
                              </span>
                            </div>

                            {/* Inline commission editor */}
                            {editingCommission === p.id && (
                              <div style={{ marginTop: '12px', padding: '14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', marginBottom: '10px' }}>
                                  Agreed commission rate — {p.name} → {selectedCompany.name}
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                                  <div>
                                    <label style={labelStyle}>Type</label>
                                    <select value={commissionForm.commission_type} onChange={e => setCommissionForm(f => ({ ...f, commission_type: e.target.value }))} style={inputStyle}>
                                      <option value="fixed">Fixed (£)</option>
                                      <option value="percentage">Percentage (%)</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label style={labelStyle}>Value {commissionForm.commission_type === 'percentage' ? '(%)' : '(£)'}</label>
                                    <input
                                      type="number" step="0.01" min="0"
                                      value={commissionForm.commission_value}
                                      onChange={e => setCommissionForm(f => ({ ...f, commission_value: e.target.value }))}
                                      style={inputStyle} placeholder={commissionForm.commission_type === 'percentage' ? 'e.g. 25' : 'e.g. 150'}
                                    />
                                  </div>
                                </div>
                                <div style={{ marginBottom: '10px' }}>
                                  <label style={labelStyle}>Notes (optional)</label>
                                  <input
                                    value={commissionForm.commission_notes}
                                    onChange={e => setCommissionForm(f => ({ ...f, commission_notes: e.target.value }))}
                                    style={inputStyle} placeholder="e.g. 25% of conveyancing fee on completed cases"
                                  />
                                </div>
                                <button onClick={() => saveCommission(selectedCompany.id, p.id)} disabled={saving} style={btnGreen}>
                                  {saving ? 'Saving…' : 'Save rate'}
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>)}

        {/* ── Category Modal ───────────────────────────────── */}
        {showCatModal && (
          <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) setShowCatModal(false); }}>
            <div style={modalStyle}>
              <div style={modalHeader}>
                <h2 style={{ fontSize: '18px', fontWeight: 700 }}>{editCat ? 'Edit Category' : 'New Category'}</h2>
                <button onClick={() => setShowCatModal(false)} style={closeBtn}>&times;</button>
              </div>
              {formError && <div style={alertRed}>{formError}</div>}
              <form onSubmit={saveCat}>
                <div style={grid2}>
                  <div style={fieldWrap}>
                    <label style={labelStyle}>Category name *</label>
                    <input required value={catForm.name} onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} placeholder="e.g. Will Writing" />
                  </div>
                  <div style={fieldWrap}>
                    <label style={labelStyle}>Icon (emoji)</label>
                    <input value={catForm.icon} onChange={e => setCatForm(f => ({ ...f, icon: e.target.value }))} style={inputStyle} placeholder="📜" />
                  </div>
                </div>
                <div style={fieldWrap}>
                  <label style={labelStyle}>Description</label>
                  <input value={catForm.description} onChange={e => setCatForm(f => ({ ...f, description: e.target.value }))} style={inputStyle} />
                </div>
                <button type="submit" disabled={saving} style={{ ...btnGreen, width: '100%', marginTop: '8px' }}>{saving ? 'Saving…' : 'Save'}</button>
              </form>
            </div>
          </div>
        )}

        {/* ── Partner Modal (rich profile + AI fill) ──────── */}
        {showPartnerModal && (
          <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) setShowPartnerModal(false); }}>
            <div style={{ ...modalStyle, maxWidth: '640px' }}>
              <div style={modalHeader}>
                <h2 style={{ fontSize: '18px', fontWeight: 700 }}>{editPartner ? 'Edit Partner Profile' : 'New Partner'}</h2>
                <button onClick={() => setShowPartnerModal(false)} style={closeBtn}>&times;</button>
              </div>

              {/* AI Auto-fill strip */}
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '14px', marginBottom: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#065f46', marginBottom: '8px' }}>
                  ✨ Auto-fill profile from website
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    value={aiUrl}
                    onChange={e => setAiUrl(e.target.value)}
                    style={{ ...inputStyle, flex: 1, background: '#fff' }}
                    placeholder="https://jacksongiles.co.uk"
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), runAiFill())}
                  />
                  <button
                    type="button"
                    onClick={runAiFill}
                    disabled={aiFilling}
                    style={{ ...btnGreen, whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    {aiFilling ? 'Fetching…' : 'Auto-fill'}
                  </button>
                </div>
                {aiError && <div style={{ fontSize: '12px', color: '#991b1b', marginTop: '6px' }}>{aiError}</div>}
                {!aiError && !aiFilling && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px' }}>Fetches the website and uses AI to extract the profile. Review and edit before saving.</div>}
              </div>

              {formError && <div style={alertRed}>{formError}</div>}

              <form onSubmit={savePartner}>
                {/* Basic info */}
                <div style={sectionHead}>Basic information</div>
                <div style={grid2}>
                  <div style={fieldWrap}>
                    <label style={labelStyle}>Category *</label>
                    <select required value={partnerForm.category_id} onChange={e => setPartnerForm(f => ({ ...f, category_id: e.target.value }))} style={inputStyle}>
                      <option value="">Select…</option>
                      {categories.filter(c => c.active).map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                    </select>
                  </div>
                  <div style={fieldWrap}>
                    <label style={labelStyle}>Partner name *</label>
                    <input required value={partnerForm.name} onChange={e => setPartnerForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} />
                  </div>
                  <div style={fieldWrap}>
                    <label style={labelStyle}>Contact name</label>
                    <input value={partnerForm.contact_name} onChange={e => setPartnerForm(f => ({ ...f, contact_name: e.target.value }))} style={inputStyle} />
                  </div>
                  <div style={fieldWrap}>
                    <label style={labelStyle}>Contact email</label>
                    <input type="email" value={partnerForm.contact_email} onChange={e => setPartnerForm(f => ({ ...f, contact_email: e.target.value }))} style={inputStyle} />
                  </div>
                </div>
                <div style={fieldWrap}>
                  <label style={labelStyle}>Website</label>
                  <input value={partnerForm.website} onChange={e => setPartnerForm(f => ({ ...f, website: e.target.value }))} style={inputStyle} placeholder="https://…" />
                </div>

                {/* Brochure content */}
                <div style={sectionHead}>Brochure content (shown to brokers)</div>
                <div style={fieldWrap}>
                  <label style={labelStyle}>About</label>
                  <textarea value={partnerForm.about} onChange={e => setPartnerForm(f => ({ ...f, about: e.target.value }))} style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} placeholder="2–3 sentence description shown on the partner brochure page" />
                </div>

                <div style={fieldWrap}>
                  <label style={labelStyle}>USPs / Key selling points</label>
                  {partnerForm.usp_bullets.map((b, i) => (
                    <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                      <input value={b} onChange={e => setBullet(i, e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder={`USP ${i + 1}`} />
                      {partnerForm.usp_bullets.length > 1 && (
                        <button type="button" onClick={() => removeBullet(i)} style={{ ...btnSmallGhost, color: '#991b1b', padding: '4px 10px' }}>✕</button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={addBullet} style={{ ...btnSmallGhost, fontSize: '12px' }}>+ Add bullet</button>
                </div>

                <div style={fieldWrap}>
                  <label style={labelStyle}>Services offered</label>
                  {partnerForm.services.map((s, i) => (
                    <div key={i} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                        <input value={s.name} onChange={e => setService(i, 'name', e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="Service name" />
                        <input value={s.price || ''} onChange={e => setService(i, 'price', e.target.value)} style={{ ...inputStyle, width: '120px' }} placeholder="Price / range" />
                        {partnerForm.services.length > 1 && (
                          <button type="button" onClick={() => removeService(i)} style={{ ...btnSmallGhost, color: '#991b1b', padding: '4px 10px' }}>✕</button>
                        )}
                      </div>
                      <input value={s.description || ''} onChange={e => setService(i, 'description', e.target.value)} style={inputStyle} placeholder="Brief description" />
                    </div>
                  ))}
                  <button type="button" onClick={addService} style={{ ...btnSmallGhost, fontSize: '12px' }}>+ Add service</button>
                </div>

                {/* Commission */}
                <div style={sectionHead}>Default commission (can be overridden per firm)</div>
                <div style={grid2}>
                  <div style={fieldWrap}>
                    <label style={labelStyle}>Commission type</label>
                    <select value={partnerForm.commission_type} onChange={e => setPartnerForm(f => ({ ...f, commission_type: e.target.value }))} style={inputStyle}>
                      {COMMISSION_TYPES.map(t => <option key={t} value={t}>{t === 'fixed' ? 'Fixed (£)' : 'Percentage (%)'}</option>)}
                    </select>
                  </div>
                  <div style={fieldWrap}>
                    <label style={labelStyle}>Default value {partnerForm.commission_type === 'percentage' ? '(%)' : '(£)'}</label>
                    <input type="number" step="0.01" min="0" value={partnerForm.commission_value} onChange={e => setPartnerForm(f => ({ ...f, commission_value: e.target.value }))} style={inputStyle} />
                  </div>
                </div>
                <div style={fieldWrap}>
                  <label style={labelStyle}>Commission notes</label>
                  <textarea value={partnerForm.commission_notes} onChange={e => setPartnerForm(f => ({ ...f, commission_notes: e.target.value }))} style={{ ...inputStyle, minHeight: '50px', resize: 'vertical' }} placeholder="e.g. 25% of conveyancing fee on completed cases only" />
                </div>

                <div style={fieldWrap}>
                  <label style={labelStyle}>Internal notes</label>
                  <textarea value={partnerForm.notes} onChange={e => setPartnerForm(f => ({ ...f, notes: e.target.value }))} style={{ ...inputStyle, minHeight: '50px', resize: 'vertical' }} />
                </div>

                <button type="submit" disabled={saving} style={{ ...btnGreen, width: '100%', marginTop: '8px' }}>
                  {saving ? 'Saving…' : (editPartner ? 'Save changes' : 'Create partner')}
                </button>
              </form>
            </div>
          </div>
        )}

          {tab === 'analytics' && (
            <div>
              {/* Headline + Period filter */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#0e1b2c', margin: 0 }}>Partner Network Revenue</h2>
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0 0' }}>Track earnings generated through the BrokerConnect partner network</p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[['all','All time'],['90','Last 90 days'],['30','Last 30 days']].map(([val,label]) => (
                    <button key={val} onClick={() => setAnalyticsPeriod(val)}
                      style={{ padding: '7px 14px', borderRadius: '6px', border: '1px solid', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                        background: analyticsPeriod === val ? '#0e1b2c' : '#fff',
                        color: analyticsPeriod === val ? '#fff' : '#374151',
                        borderColor: analyticsPeriod === val ? '#0e1b2c' : '#d1d5db' }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {analyticsLoading && <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>Loading analytics…</div>}

              {!analyticsLoading && analyticsData && (() => {
                const t = analyticsData.totals;
                const fmt = n => '£' + parseFloat(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                const pct = (a, b) => b > 0 ? Math.round((a / b) * 100) + '%' : '—';
                return (
                  <>
                    {/* Headline stat cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px', marginBottom: '32px' }}>
                      {[
                        { label: 'Total referrals', value: t.total_referrals, sub: `${t.completed_referrals} completed`, colour: '#0e1b2c' },
                        { label: 'Commission paid to brokers', value: fmt(t.total_commission), sub: 'Sum across all completed', colour: '#10b981' },
                        { label: 'Introducer payouts', value: fmt(t.total_introducer), sub: 'Broker-to-introducer share', colour: '#6366f1' },
                        { label: 'Network conversion', value: pct(parseInt(t.completed_referrals), parseInt(t.total_referrals)), sub: 'Referrals → completed', colour: '#f59e0b' },
                      ].map(card => (
                        <div key={card.label} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '18px', borderTop: `3px solid ${card.colour}` }}>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '8px' }}>{card.label}</div>
                          <div style={{ fontSize: '26px', fontWeight: 800, color: '#0e1b2c', marginBottom: '4px' }}>{card.value}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>{card.sub}</div>
                        </div>
                      ))}
                    </div>

                    {/* Per-partner table */}
                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', marginBottom: '28px', overflow: 'hidden' }}>
                      <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '16px', fontWeight: 700, color: '#0e1b2c' }}>Partner Earnings</span>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>How much each partner has paid out in broker commissions via BrokerConnect</span>
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ background: '#f9fafb' }}>
                            {['Partner','Category','Referrals','Completed','Commission paid to brokers','Introducer share paid'].map(h => (
                              <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '.04em' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsData.partners.length === 0 && (
                            <tr><td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#9ca3af' }}>No referral data yet</td></tr>
                          )}
                          {analyticsData.partners.map((p, i) => (
                            <tr key={p.partner_id} style={{ borderTop: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                              <td style={{ padding: '12px 16px', fontWeight: 600, color: '#0e1b2c' }}>{p.partner_name}</td>
                              <td style={{ padding: '12px 16px' }}>
                                <span style={{ background: '#f0fdf4', color: '#065f46', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '99px' }}>{p.category || '—'}</span>
                              </td>
                              <td style={{ padding: '12px 16px', color: '#374151' }}>{p.total_referrals}</td>
                              <td style={{ padding: '12px 16px', color: '#374151' }}>{p.completed_referrals}</td>
                              <td style={{ padding: '12px 16px', fontWeight: 700, color: parseFloat(p.total_commission_paid) > 0 ? '#10b981' : '#9ca3af' }}>
                                {fmt(p.total_commission_paid)}
                              </td>
                              <td style={{ padding: '12px 16px', color: parseFloat(p.total_introducer_paid) > 0 ? '#6366f1' : '#9ca3af' }}>
                                {fmt(p.total_introducer_paid)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Per-broker table */}
                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
                      <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '16px', fontWeight: 700, color: '#0e1b2c' }}>Broker Firm Earnings</span>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>Commission earned by each broker firm from partner introductions</span>
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ background: '#f9fafb' }}>
                            {['Broker firm','Referrals made','Completed','Partners used','Commission earned','Introducer passed on'].map(h => (
                              <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '.04em' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsData.brokers.length === 0 && (
                            <tr><td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#9ca3af' }}>No referral data yet</td></tr>
                          )}
                          {analyticsData.brokers.map((b, i) => (
                            <tr key={b.company_id} style={{ borderTop: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                              <td style={{ padding: '12px 16px', fontWeight: 600, color: '#0e1b2c' }}>{b.company_name}</td>
                              <td style={{ padding: '12px 16px', color: '#374151' }}>{b.total_referrals}</td>
                              <td style={{ padding: '12px 16px', color: '#374151' }}>{b.completed_referrals}</td>
                              <td style={{ padding: '12px 16px', color: '#374151' }}>{b.partners_used}</td>
                              <td style={{ padding: '12px 16px', fontWeight: 700, color: parseFloat(b.total_earned) > 0 ? '#10b981' : '#9ca3af' }}>
                                {fmt(b.total_earned)}
                              </td>
                              <td style={{ padding: '12px 16px', color: parseFloat(b.introducer_earned) > 0 ? '#6366f1' : '#9ca3af' }}>
                                {fmt(b.introducer_earned)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
{/* ── Partner Invite Modal ── */}
      {invitePartner && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '14px', padding: '28px', maxWidth: '440px', width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '17px', fontWeight: 700, color: '#0e1b2c', margin: 0 }}>Invite partner to portal</h2>
              <button onClick={() => setInvitePartner(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>×</button>
            </div>

            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '20px' }}>
              Send a signup link to the portal contact for <strong style={{ color: '#0e1b2c' }}>{invitePartner.name}</strong>.
              They'll set their password and accept the Terms of Business.
            </p>

            {inviteResult ? (
              <div>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#065f46', marginBottom: '8px' }}>✓ Invite sent to {inviteForm.email}</div>
                  <div style={{ fontSize: '12px', color: '#374151', marginBottom: '6px' }}>Signup link (also emailed):</div>
                  <div style={{ fontSize: '11px', color: '#6b7280', wordBreak: 'break-all', background: '#fff', padding: '8px', borderRadius: '6px', border: '1px solid #e5e7eb', fontFamily: 'monospace' }}>
                    {inviteResult.link}
                  </div>
                  <button onClick={() => navigator.clipboard?.writeText(inviteResult.link)}
                    style={{ marginTop: '8px', background: 'none', border: '1px solid #d1d5db', borderRadius: '6px', padding: '4px 12px', fontSize: '11px', cursor: 'pointer', color: '#374151' }}>
                    Copy link
                  </button>
                </div>
                <button onClick={() => setInvitePartner(null)} style={{ width: '100%', padding: '10px', background: '#0e1b2c', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
                  Done
                </button>
              </div>
            ) : (
              <div>
                {inviteError && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '14px' }}>{inviteError}</div>}
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Contact name</label>
                  <input value={inviteForm.contact_name} onChange={e => setInviteForm(f => ({ ...f, contact_name: e.target.value }))}
                    placeholder="Full name of portal contact"
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Email address</label>
                  <input type="email" value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="their@email.com"
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => setInvitePartner(null)} style={{ flex: 1, padding: '10px', background: 'none', border: '1px solid #d1d5db', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', color: '#374151' }}>
                    Cancel
                  </button>
                  <button onClick={sendInvite} disabled={inviteSending}
                    style={{ flex: 2, padding: '10px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', opacity: inviteSending ? 0.7 : 1 }}>
                    {inviteSending ? 'Sending…' : 'Send invite'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </main>
    </div>
  );
}


const sectionHead = { fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.05em', margin: '20px 0 12px 0', paddingBottom: '8px', borderBottom: '1px solid #f3f4f6' };
const alertRed = { background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' };
const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' };
const modalStyle = { background: '#fff', borderRadius: '14px', padding: '24px', maxWidth: '480px', width: '100%', maxHeight: '90vh', overflowY: 'auto' };
const modalHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' };
const closeBtn = { background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1 };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' };
const fieldWrap = { marginBottom: '14px' };
const labelStyle = { display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' };
const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit', color: '#111827', background: '#fff', boxSizing: 'border-box' };
const btnGreen = { background: '#10b981', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 20px', fontWeight: 600, fontSize: '14px', cursor: 'pointer' };
const btnSmallGhost = { background: 'none', border: '1px solid #d1d5db', borderRadius: '6px', padding: '5px 12px', fontSize: '12px', cursor: 'pointer', color: '#374151', fontWeight: 500 };
